import { Button, FormField, Modal, SearchInput, Select, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROLE_KEYS,
  productionTaskKindSchema,
  productionTaskStatusSchema,
  type ProductionTaskListItem,
} from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { completeTask, createTask, fetchTasks, productionKeys, updateTask } from '../api';
import { TaskBoard } from '../components/TaskBoard';
import { PageHeader } from '../../../components/PageHeader';
import { useAuthUser, usePermission } from '../../../auth/auth-store';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useListState } from '../../../hooks/useListState';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The production task board. Search/filter/paging still go through the shared
 * list contract (`useListState` → `listQueryParams`), so the board is just a
 * different rendering of the same server-side list every other page uses.
 */
export function ProductionTasksPage(): ReactNode {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const canManage = usePermission('production.tasks.manage', 'production.requests.manage');
  const canCreate = usePermission('production.tasks.manage');

  const { state, setState, setFilter } = useListState({ defaultSort: 'dueAt', defaultPageSize: 200 });
  const [creating, setCreating] = useState(false);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);

  const query = useQuery({ queryKey: productionKeys.tasks(state), queryFn: () => fetchTasks(state) });
  useLiveQuery(['production'], ['production', 'tasks']);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: productionKeys.all });
  };

  const mutate = useMutation({
    mutationFn: async ({
      task,
      action,
    }: {
      task: ProductionTaskListItem;
      action: 'start' | 'complete' | 'reopen' | 'assignToMe';
    }) => {
      if (action === 'complete') return completeTask(task.id);
      if (action === 'start') return updateTask(task.id, { status: 'in_progress' });
      if (action === 'reopen') return updateTask(task.id, { status: 'open' });
      return updateTask(task.id, { assignedUser: user?.id ?? null });
    },
    onMutate: ({ task }) => {
      setBusyTaskId(task.id);
    },
    onSuccess: (_result, { action }) => {
      toastSuccess(action === 'complete' ? t('tasks.toasts.completed') : t('tasks.toasts.updated'));
      refresh();
    },
    onError: (err) => {
      toastError(err);
    },
    onSettled: () => {
      setBusyTaskId(null);
    },
  });

  return (
    <>
      <PageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('tasks.new')}
            </Button>
          ) : null
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          variant="outline"
          className="w-full sm:w-72"
          value={state.q ?? ''}
          onChange={(value) => {
            setState({ ...state, q: value || undefined, page: 1 });
          }}
          onClear={() => {
            setState({ ...state, q: undefined, page: 1 });
          }}
          placeholder={t('tasks.searchPlaceholder')}
          aria-label={t('tasks.searchPlaceholder')}
        />
        <Select
          aria-label={t('tasks.filters.status')}
          className="w-44"
          options={[
            { value: '', label: t('tasks.filters.allStatuses') },
            ...productionTaskStatusSchema.options.map((status) => ({
              value: status,
              label: t(`tasks.status.${status}`),
            })),
          ]}
          value={String(state.filters?.status ?? '')}
          onChange={(event) => {
            setFilter('status', event.target.value || undefined);
          }}
        />
        <Select
          aria-label={t('tasks.filters.kind')}
          className="w-52"
          options={[
            { value: '', label: t('tasks.filters.allKinds') },
            ...productionTaskKindSchema.options.map((kind) => ({
              value: kind,
              label: t(`tasks.kind.${kind}`),
            })),
          ]}
          value={String(state.filters?.kind ?? '')}
          onChange={(event) => {
            setFilter('kind', event.target.value || undefined);
          }}
        />
      </div>

      <TaskBoard
        tasks={query.data?.items ?? []}
        canManage={canManage}
        busyTaskId={busyTaskId}
        emptyLabel={t('tasks.empty')}
        onStart={(task) => {
          mutate.mutate({ task, action: 'start' });
        }}
        onComplete={(task) => {
          mutate.mutate({ task, action: 'complete' });
        }}
        onReopen={(task) => {
          mutate.mutate({ task, action: 'reopen' });
        }}
        onAssignToMe={
          user
            ? (task) => {
                mutate.mutate({ task, action: 'assignToMe' });
              }
            : undefined
        }
      />

      {creating ? (
        <CreateTaskModal
          onClose={() => {
            setCreating(false);
          }}
          onDone={refresh}
        />
      ) : null}
    </>
  );
}

function CreateTaskModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }): ReactNode {
  const { t } = useTranslation('production');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'unload' | 'move_to_production' | 'general'>('unload');
  const [assignedRole, setAssignedRole] = useState('');
  const [dueAt, setDueAt] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createTask({
        kind,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(assignedRole ? { assignedRole } : {}),
        // `datetime-local` has no zone; the browser's own zone is the operator's.
        ...(dueAt ? { dueAt: new Date(dueAt).toISOString() } : {}),
      }),
    onSuccess: () => {
      toastSuccess(t('tasks.toasts.created'));
      onDone();
      onClose();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('tasks.new')} width={520}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('tasks.fields.title')}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
        <Select
          label={t('tasks.fields.kind')}
          options={productionTaskKindSchema.options.map((option) => ({
            value: option,
            label: t(`tasks.kind.${option}`),
          }))}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as typeof kind);
          }}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">{t('tasks.fields.description')}</span>
          <Textarea
            rows={2}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </label>
        <Select
          label={t('tasks.fields.assignedRole')}
          placeholder={t('tasks.fields.selectRole')}
          options={[
            { value: '', label: t('tasks.fields.unassigned') },
            ...ROLE_KEYS.filter((role) => role !== 'super_admin' && role !== 'bestallare').map((role) => ({
              value: role,
              label: role,
            })),
          ]}
          value={assignedRole}
          onChange={(event) => {
            setAssignedRole(event.target.value);
          }}
        />
        <FormField
          label={t('tasks.fields.dueAt')}
          type="datetime-local"
          value={dueAt}
          onChange={(event) => {
            setDueAt(event.target.value);
          }}
        />

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('tasks.actions.cancel')}
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={!title.trim()}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {t('tasks.actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
